import { CatalogProps } from '../../utils/enhanced-ecommerce';
import { IMPRESSION_DWELL_TIME_MS, IMPRESSION_VISIBILITY_THRESHOLD } from './material-card-impression.constants';
import materialCardImpressionHelper from './material-card-impression.helper';

type ImpressionTarget = {
    item: Material;
    materialProps: CatalogProps;
    dwellTimer?: ReturnType<typeof setTimeout>;
};

// One IntersectionObserver shared by every material card. Visibility is kept here instead of in
// React state, so cards never re-render while scrolling, and nothing is observed while disabled.
const materialCardImpressionObserver = (function () {
    const targets = new Map<Element, ImpressionTarget>();
    const visibleElements = new Set<Element>();
    let observer: IntersectionObserver | null = null;
    // IntersectionObserver does not fire on tab switches, so dwell timers are paused explicitly.
    let isPaused = false;
    // AB TEST - MCP-1559 (remove on either accept or reject)
    let isEnabled = false;

    const clearDwellTimer = (target: ImpressionTarget) => {
        clearTimeout(target.dwellTimer);
        target.dwellTimer = undefined;
    };

    const startDwellTimer = (element: Element) => {
        const target = targets.get(element);

        if (!target) {
            return;
        }

        clearDwellTimer(target);

        if (isPaused || !target.materialProps.internalPath) {
            return;
        }

        target.dwellTimer = setTimeout(() => {
            target.dwellTimer = undefined;
            materialCardImpressionHelper.track(target.item, target.materialProps);
        }, IMPRESSION_DWELL_TIME_MS);
    };

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
        entries.forEach(({ target: element, isIntersecting, intersectionRatio }) => {
            const target = targets.get(element);

            if (!target) {
                return;
            }

            if (isIntersecting && intersectionRatio >= IMPRESSION_VISIBILITY_THRESHOLD) {
                if (!visibleElements.has(element)) {
                    visibleElements.add(element);
                    startDwellTimer(element);
                }

                return;
            }

            visibleElements.delete(element);
            clearDwellTimer(target);
        });
    };

    const getObserver = () => {
        if (!observer && typeof window.IntersectionObserver !== 'undefined') {
            observer = new IntersectionObserver(handleIntersection, { threshold: IMPRESSION_VISIBILITY_THRESHOLD });
        }

        return observer;
    };

    const attach = (element: Element, item: Material, materialProps: CatalogProps) => {
        targets.set(element, { item, materialProps });

        if (isEnabled) {
            getObserver()?.observe(element);
        }
    };

    // Position-only changes keep the running dwell timer; a different material restarts it.
    const update = (element: Element, item: Material, materialProps: CatalogProps) => {
        const target = targets.get(element);

        if (!target) {
            return;
        }

        const isSameImpression =
            target.item.id === item.id && target.materialProps.internalPath === materialProps.internalPath;

        target.item = item;
        target.materialProps = materialProps;

        if (!isSameImpression && visibleElements.has(element)) {
            startDwellTimer(element);
        }
    };

    const detach = (element: Element) => {
        const target = targets.get(element);

        if (target) {
            clearDwellTimer(target);
        }

        targets.delete(element);
        visibleElements.delete(element);
        observer?.unobserve(element);
    };

    // Restarts the dwell timer of every visible card, so still-mounted cards count again after
    // the dedup cache is reset for a new page view.
    const rearm = () => {
        visibleElements.forEach(startDwellTimer);
    };

    // Stops every running dwell timer while the tab is hidden, so unseen cards are not counted.
    const pause = () => {
        isPaused = true;
        targets.forEach(clearDwellTimer);
    };

    // Restarts the dwell timer of every card that is still visible once the tab is shown again.
    const resume = () => {
        isPaused = false;
        visibleElements.forEach(startDwellTimer);
    };

    // AB TEST - MCP-1559 (remove on either accept or reject)
    const setEnabled = (value: boolean) => {
        if (value === isEnabled) {
            return;
        }

        isEnabled = value;

        if (isEnabled) {
            targets.forEach((_target, element) => getObserver()?.observe(element));

            return;
        }

        observer?.disconnect();
        observer = null;
        visibleElements.clear();
        targets.forEach(clearDwellTimer);
    };

    return { attach, update, detach, rearm, pause, resume, setEnabled };
})();

export default materialCardImpressionObserver;
